/**
 * PSET-style geometry overlay: storey cut, top-down view, ray fan, mm labels.
 */
import {
  applyGrayDollhouse,
  applyStoreyCut,
  clearDollhouseTheming,
  clearStoreyCut,
  cutPlaneZ,
  focusBuildingWithElementHighlight,
  focusObliqueOnBox,
  focusTopDownOnBox,
} from "../geometry/geometryFocus";
import { clampBoxToModelShell, elementWorldBox } from "../geometry/rayCast";
import type { WashroomMeasureResult } from "../geometry/types";

const OVERLAY_SCENE = "fc2-geometry-overlay";
const EXTENSION_ID = "Fc2GeometryOverlayExtension";
const LABEL_CLASS = "fc2-dim-label";
const VIEWPORT_CHIP_CLASS = "fc2-overlay-chip";

let registered = false;

export interface OverlayViewOpts {
  /** When true, skip 3D-only camera cuts / dollhouse theming. */
  plan2d?: boolean;
  /** Visual tone — indeterminate findings use amber, not failure red. */
  tone?: "violation" | "uncertain" | "pass";
  /** Frame whole building (rooms / small elements on 3D view). */
  buildingContext?: boolean;
}

export interface GeometryOverlayExtensionApi {
  drawMeasurement(
    result: WashroomMeasureResult,
    pass: boolean | null,
    opts?: OverlayViewOpts
  ): void;
  drawPropertyViolation(
    dbId: number,
    label: string,
    detail?: string,
    opts?: OverlayViewOpts
  ): void;
  drawQuantityCheck(
    dbId: number,
    opts: {
      kind: "minimum" | "maximum";
      observedMm: number;
      thresholdMm: number;
      axis: "horizontal" | "vertical";
      propertyLabel: string;
      codeLabel: string;
      plan2d?: boolean;
    }
  ): void;
  clear(): void;
}

interface DimLabel {
  el: HTMLDivElement;
  world: THREE.Vector3;
}

function getAutodeskGlobal(): typeof Autodesk | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { Autodesk?: typeof Autodesk };
  return w.Autodesk?.Viewing?.Extension ? w.Autodesk : null;
}

function addLineSegment(
  viewer: Autodesk.Viewing.GuiViewer3D,
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
  color: number,
  depthTest: boolean
): void {
  const geo = new THREE.Geometry();
  geo.vertices.push(
    new THREE.Vector3(x1, y1, z1),
    new THREE.Vector3(x2, y2, z2)
  );
  const mat = new THREE.LineBasicMaterial({
    color,
    depthTest,
    depthWrite: false,
  });
  viewer.impl.addOverlay(OVERLAY_SCENE, new THREE.Line(geo, mat));
}

function addFilledRect(
  viewer: Autodesk.Viewing.GuiViewer3D,
  minx: number,
  miny: number,
  maxx: number,
  maxy: number,
  z: number,
  color: number,
  opacity: number
): void {
  const geo = new THREE.Geometry();
  geo.vertices.push(
    new THREE.Vector3(minx, miny, z),
    new THREE.Vector3(maxx, miny, z),
    new THREE.Vector3(maxx, maxy, z),
    new THREE.Vector3(minx, maxy, z)
  );
  geo.faces.push(new THREE.Face3(0, 1, 2));
  geo.faces.push(new THREE.Face3(0, 2, 3));
  const doubleSide =
    (THREE as unknown as { DoubleSide?: number }).DoubleSide ?? 2;
  const mat = new THREE.MeshBasicMaterial({
    color,
    opacity,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    side: doubleSide,
  });
  viewer.impl.addOverlay(OVERLAY_SCENE, new THREE.Mesh(geo, mat));
}

export function registerGeometryOverlayExtension(): void {
  if (registered) return;

  const AutodeskGlobal = getAutodeskGlobal();
  if (!AutodeskGlobal) {
    console.warn(
      "[Vera] Forge Viewer SDK not loaded — geometry overlay not registered"
    );
    return;
  }

  const Viewing = AutodeskGlobal.Viewing;

  class GeometryOverlayExtension extends Viewing.Extension {
    static extensionId = EXTENSION_ID;
    private dimLabels: DimLabel[] = [];
    private dimLabelHandler: (() => void) | null = null;
    private viewportChip: HTMLDivElement | null = null;
    private readonly viewing = Viewing;

    load(): boolean {
      return true;
    }

    unload(): boolean {
      this.clear();
      return true;
    }

    private clearDimLabels(): void {
      if (this.dimLabelHandler && this.viewer) {
        this.viewer.removeEventListener(
          this.viewing.CAMERA_CHANGE_EVENT,
          this.dimLabelHandler
        );
        this.dimLabelHandler = null;
      }
      for (const l of this.dimLabels) {
        l.el.remove();
      }
      this.dimLabels = [];
      this.viewer?.container
        ?.querySelectorAll(`.${LABEL_CLASS}`)
        .forEach(el => {
          el.remove();
        });
    }

    private clearViewportChip(): void {
      this.viewportChip?.remove();
      this.viewportChip = null;
    }

    /** Small fixed chip — stays in a corner so it never covers the model. */
    private setViewportChip(text: string, bg: string): void {
      this.clearViewportChip();
      const v = this.viewer;
      if (!v?.container || !text.trim()) return;

      const el = document.createElement("div");
      el.className = VIEWPORT_CHIP_CLASS;
      el.style.cssText = [
        "position:absolute",
        "left:10px",
        "top:10px",
        "color:#fff",
        "font:600 11px/1.35 system-ui,sans-serif",
        `background:${bg}`,
        "padding:5px 9px",
        "border-radius:5px",
        "pointer-events:none",
        "z-index:25",
        "white-space:pre-line",
        "max-width:210px",
        "box-shadow:0 2px 10px rgba(0,0,0,0.45)",
      ].join(";");
      el.textContent = text;
      v.container.appendChild(el);
      this.viewportChip = el;
    }

    private setDimLabels(
      specs: Array<{ world: THREE.Vector3; text: string }>,
      bg: string
    ): void {
      this.clearDimLabels();
      const v = this.viewer;
      if (!v?.container) return;

      for (const sp of specs) {
        const el = document.createElement("div");
        el.className = LABEL_CLASS;
        el.style.cssText = [
          "position:absolute",
          "color:#fff",
          "font:bold 13px sans-serif",
          `background:${bg}`,
          "padding:2px 7px",
          "border-radius:3px",
          "pointer-events:none",
          "transform:translate(-50%,-50%)",
          "z-index:30",
          "white-space:pre-line",
          "text-align:center",
          "line-height:1.35",
          "max-width:220px",
        ].join(";");
        el.textContent = sp.text;
        v.container.appendChild(el);
        this.dimLabels.push({ el, world: sp.world });
      }

      this.dimLabelHandler = () => {
        for (const l of this.dimLabels) {
          const p = v.worldToClient(l.world);
          l.el.style.left = `${p.x}px`;
          l.el.style.top = `${p.y}px`;
        }
      };
      v.addEventListener(
        this.viewing.CAMERA_CHANGE_EVENT,
        this.dimLabelHandler
      );
      this.dimLabelHandler();
    }

    clear(): void {
      this.clearDimLabels();
      this.clearViewportChip();
      if (this.viewer) {
        clearStoreyCut(this.viewer);
        clearDollhouseTheming(this.viewer);
      }
      if (this.viewer?.impl) {
        this.viewer.impl.removeOverlayScene(OVERLAY_SCENE);
        this.viewer.impl.invalidate(true, true, true);
      }
    }

    /**
     * Avoid zooming F2D sheets into microscopic annotation bboxes (sheet
     * numbers, grid bubbles, dimension text).
     */
    private fitPlan2d(
      v: Autodesk.Viewing.GuiViewer3D,
      dbId: number,
      box: THREE.Box3
    ): void {
      const modelBox = v.model?.getBoundingBox?.();
      if (!modelBox) {
        v.fitToView([dbId], undefined, true);
        return;
      }
      const elem = box.getSize(new THREE.Vector3());
      const model = modelBox.getSize(new THREE.Vector3());
      const elemArea = Math.max(elem.x, 0) * Math.max(elem.y, 0);
      const modelArea = Math.max(model.x, 0) * Math.max(model.y, 0);
      const ratio = modelArea > 0 ? elemArea / modelArea : 1;

      if (ratio < 0.002) {
        const span = Math.max(model.x, model.y) * 0.22;
        const c = box.getCenter(new THREE.Vector3());
        const window = new THREE.Box3(
          new THREE.Vector3(c.x - span / 2, c.y - span / 2, modelBox.min.z),
          new THREE.Vector3(c.x + span / 2, c.y + span / 2, modelBox.max.z)
        );
        v.navigation.fitBounds(true, window);
        return;
      }
      v.fitToView([dbId], undefined, true);
    }

    private focusElement(
      v: Autodesk.Viewing.GuiViewer3D,
      dbId: number,
      box: THREE.Box3,
      plan2d: boolean,
      buildingContext = false
    ): void {
      if (plan2d) {
        v.select([dbId]);
        this.fitPlan2d(v, dbId, box);
        return;
      }
      if (buildingContext) {
        focusBuildingWithElementHighlight(v, dbId);
        return;
      }
      clearStoreyCut(v);
      applyGrayDollhouse(v, dbId);
      focusObliqueOnBox(v, box);
    }

    /** Plan footprint only — avoids a full 3D cage that sticks out past the roof line. */
    private drawPlanFootprint(
      v: Autodesk.Viewing.GuiViewer3D,
      box: THREE.Box3,
      z: number,
      lineColor: number,
      fillColor?: number
    ): void {
      const { min, max } = box;
      if (fillColor != null) {
        addFilledRect(v, min.x, min.y, max.x, max.y, z, fillColor, 0.22);
      }
      const outline = [
        [min.x, min.y, z, max.x, min.y, z],
        [max.x, min.y, z, max.x, max.y, z],
        [max.x, max.y, z, min.x, max.y, z],
        [min.x, max.y, z, min.x, min.y, z],
      ] as const;
      for (const [x1, y1, z1, x2, y2, z2] of outline) {
        addLineSegment(v, x1, y1, z1, x2, y2, z2, lineColor, false);
      }
    }

    drawMeasurement(
      result: WashroomMeasureResult,
      pass: boolean | null,
      viewOpts?: OverlayViewOpts
    ): void {
      this.clear();
      const geom = result.geometry;
      if (!geom || !this.viewer?.impl) return;

      const v = this.viewer;
      const model = v.model;
      const unit = model.getUnitScale?.() ?? 1;
      const dbId = result.candidate.dbId;
      const plan2d = viewOpts?.plan2d === true;

      const fixtureBox = elementWorldBox(v, dbId) ?? geom.box.clone();
      const viewBox = fixtureBox.clone().union(geom.box);
      if (plan2d) {
        v.select([dbId]);
        v.fitToView([dbId], undefined, true);
      } else {
        applyStoreyCut(v, fixtureBox);
        applyGrayDollhouse(v, dbId);
        focusTopDownOnBox(v, viewBox);
      }

      v.impl.createOverlayScene(OVERLAY_SCENE);

      const passColor = 0x22cc22;
      const failColor = 0xff2020;
      const rectColor = pass === null ? 0xfbbf24 : pass ? passColor : failColor;
      const labelBg =
        pass === null
          ? "rgba(251,191,36,0.95)"
          : pass
            ? "rgba(34,204,34,0.95)"
            : "rgba(255,32,32,0.95)";

      const { min, max } = geom.box;
      const zCut = plan2d ? (min.z + max.z) / 2 : cutPlaneZ(fixtureBox, unit);
      const origin =
        result.rayOrigin ??
        fixtureBox.getCenter(new THREE.Vector3()).setZ(min.z + 0.75 / unit);
      const ox = origin.x;
      const oy = origin.y;
      const zRay = plan2d ? zCut : origin.z;

      if (!plan2d) {
        for (const p of geom.points) {
          addLineSegment(v, ox, oy, zRay, p.x, p.y, zRay, 0x20dd20, true);
        }
      }

      addFilledRect(v, min.x, min.y, max.x, max.y, zCut, rectColor, 0.35);

      const outline = [
        [min.x, min.y, zCut, max.x, min.y, zCut],
        [max.x, min.y, zCut, max.x, max.y, zCut],
        [max.x, max.y, zCut, min.x, max.y, zCut],
        [min.x, max.y, zCut, min.x, min.y, zCut],
      ] as const;
      for (const [x1, y1, z1, x2, y2, z2] of outline) {
        addLineSegment(v, x1, y1, z1, x2, y2, z2, rectColor, false);
      }

      if (result.shortMm != null && result.longMm != null) {
        const cx = (min.x + max.x) / 2;
        const cy = (min.y + max.y) / 2;
        const wMm = result.widthMm ?? Math.round((max.x - min.x) * 304.8);
        const dMm = result.depthMm ?? Math.round((max.y - min.y) * 304.8);
        const xIsLong = wMm >= dMm;
        this.setDimLabels(
          [
            {
              world: new THREE.Vector3(cx, max.y, zCut),
              text: `${xIsLong ? result.longMm : result.shortMm} mm`,
            },
            {
              world: new THREE.Vector3(min.x, cy, zCut),
              text: `${xIsLong ? result.shortMm : result.longMm} mm`,
            },
          ],
          labelBg
        );
      }

      v.clearSelection();
      v.impl.invalidate(true, true, true);
    }

    drawPropertyViolation(
      dbId: number,
      label: string,
      detail?: string,
      viewOpts?: OverlayViewOpts
    ): void {
      this.clear();
      const v = this.viewer;
      if (!v?.impl) return;

      const box = elementWorldBox(v, dbId);
      if (!box) {
        v.select([dbId]);
        v.fitToView([dbId]);
        return;
      }

      const plan2d = viewOpts?.plan2d === true;
      const buildingContext = viewOpts?.buildingContext === true;
      const tone = viewOpts?.tone ?? "violation";
      const toneColors = {
        violation: {
          line: 0xff2020,
          fill: 0xff2020,
          label: "rgba(255,32,32,0.95)",
        },
        uncertain: {
          line: 0xfbbf24,
          fill: 0xfbbf24,
          label: "rgba(251,191,36,0.95)",
        },
        pass: { line: 0x22cc66, fill: 0x22cc66, label: "rgba(34,204,34,0.95)" },
      } as const;
      const colors = toneColors[tone];

      const drawBox = clampBoxToModelShell(v, box);
      this.focusElement(v, dbId, drawBox, plan2d, buildingContext);

      v.impl.createOverlayScene(OVERLAY_SCENE);

      const c = drawBox.getCenter(new THREE.Vector3());
      const model = v.model;
      const unit = model.getUnitScale?.() ?? 1;
      const z = plan2d
        ? (drawBox.min.z + drawBox.max.z) / 2
        : cutPlaneZ(drawBox, unit);

      this.drawPlanFootprint(v, drawBox, z, colors.line, colors.fill);

      const labelText = (detail ?? label).trim();
      if (plan2d && labelText) {
        this.setDimLabels(
          [
            {
              world: new THREE.Vector3(c.x, drawBox.max.y, z),
              text: labelText,
            },
          ],
          colors.label
        );
      } else if (labelText) {
        this.setViewportChip(labelText, colors.label);
      }

      v.select([dbId]);
      v.impl.invalidate(true, true, true);
    }

    drawQuantityCheck(
      dbId: number,
      opts: {
        kind: "minimum" | "maximum";
        observedMm: number;
        thresholdMm: number;
        axis: "horizontal" | "vertical";
        propertyLabel: string;
        codeLabel: string;
        plan2d?: boolean;
      }
    ): void {
      this.clear();
      const v = this.viewer;
      if (!v?.impl) return;

      const box = elementWorldBox(v, dbId);
      if (!box) {
        v.select([dbId]);
        v.fitToView([dbId]);
        return;
      }

      const plan2d = opts.plan2d === true;
      this.focusElement(v, dbId, box, plan2d);

      v.impl.createOverlayScene(OVERLAY_SCENE);

      const c = box.getCenter(new THREE.Vector3());
      const pad = Math.max(box.max.x - box.min.x, box.max.y - box.min.y) * 0.12;
      const red = 0xff3030;
      const green = 0x22cc66;
      const z = (box.min.z + box.max.z) / 2;
      const limitWord = opts.kind === "minimum" ? "min" : "max";

      if (opts.axis === "vertical" && !plan2d) {
        const x = box.max.x + pad;
        const y = c.y;
        const zObs = box.min.z + opts.observedMm / 304.8;
        const zReq = box.min.z + opts.thresholdMm / 304.8;
        addLineSegment(v, x, y, box.min.z, x, y, zObs, red, false);
        addLineSegment(
          v,
          x + pad * 0.3,
          y,
          box.min.z,
          x + pad * 0.3,
          y,
          zReq,
          green,
          false
        );
        this.setDimLabels(
          [
            {
              world: new THREE.Vector3(x, y, zObs),
              text: `${opts.observedMm} mm`,
            },
            {
              world: new THREE.Vector3(x + pad * 0.3, y, zReq),
              text: `${opts.thresholdMm} mm ${limitWord}`,
            },
          ],
          "rgba(0,0,0,0.75)"
        );
      } else {
        const yLine = plan2d ? box.max.y + pad : c.y;
        const xObs =
          opts.kind === "minimum"
            ? box.min.x + opts.observedMm / 304.8
            : box.max.x - opts.observedMm / 304.8;
        const xReq =
          opts.kind === "minimum"
            ? box.min.x + opts.thresholdMm / 304.8
            : box.max.x - opts.thresholdMm / 304.8;
        addLineSegment(v, box.min.x, yLine, z, xObs, yLine, z, red, false);
        addLineSegment(
          v,
          box.min.x,
          yLine + pad * 0.25,
          z,
          xReq,
          yLine + pad * 0.25,
          z,
          green,
          false
        );
        this.setDimLabels(
          [
            {
              world: new THREE.Vector3(
                (box.min.x + xObs) / 2,
                yLine,
                z + pad * 0.2
              ),
              text: `${opts.observedMm} mm`,
            },
            {
              world: new THREE.Vector3(
                (box.min.x + xReq) / 2,
                yLine + pad * 0.25,
                z + pad * 0.2
              ),
              text: `${opts.thresholdMm} mm ${limitWord}`,
            },
          ],
          "rgba(0,0,0,0.75)"
        );
      }

      v.select([dbId]);
      v.impl.invalidate(true, true, true);
    }
  }

  if (
    Viewing.theExtensionManager.registerExtension(
      EXTENSION_ID,
      GeometryOverlayExtension
    )
  ) {
    registered = true;
  }
}

export async function getGeometryOverlayExtension(
  viewer: Autodesk.Viewing.GuiViewer3D
): Promise<GeometryOverlayExtensionApi | null> {
  registerGeometryOverlayExtension();
  const ext = (await viewer.loadExtension(
    EXTENSION_ID
  )) as unknown as GeometryOverlayExtensionApi | null;
  return ext;
}
